import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./icons";
import * as email from "../lib/emailClient";

/* Compose + send from the rep's connected mailbox. Self-loads mailbox status so
 * it can prompt to connect one when there isn't. Used from Settings and from an
 * account's contacts. */
export interface Prefill { to?: string; toName?: string; subject?: string; body?: string; accountId?: string; }

export function Compose({ prefill, onClose, onSent }: { prefill?: Prefill; onClose: () => void; onSent?: (e: email.OutboxEmail) => void }) {
  const [mode, setMode] = useState<email.Mode>("local");
  const [mailbox, setMailbox] = useState<email.Mailbox | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [to, setTo] = useState(prefill?.to ?? "");
  const [subject, setSubject] = useState(prefill?.subject ?? "");
  const [body, setBody] = useState(prefill?.body ?? "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<email.OutboxEmail | null>(null);

  useEffect(() => { email.loadMailbox().then((r) => { setMode(r.mode); setMailbox(r.mailbox); setLoaded(true); }); }, []);

  const send = async () => {
    setSending(true);
    const e = await email.sendEmail(mode, { to: to.trim(), subject: subject.trim(), body, accountId: prefill?.accountId });
    setSending(false); setSent(e); onSent?.(e);
  };
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim()) && subject.trim();

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}>
            <Icon name="mail" size={16} />
            <b>New email{prefill?.toName ? ` to ${prefill.toName}` : ""}</b>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>

        {sent ? (
          <div className="compose-modal__bd" style={{ textAlign: "center", padding: "34px 20px" }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--good-soft)", color: "var(--good)", display: "grid", placeItems: "center", margin: "0 auto 14px" }}><Icon name="check" size={22} /></div>
            <b style={{ fontSize: 15 }}>{sent.simulated ? "Recorded in your outbox" : "Sent"}</b>
            <p className="faint" style={{ fontSize: 13, marginTop: 6 }}>
              {sent.simulated ? "Connect a live mailbox to actually deliver — this was recorded so you can see the flow." : `Delivered from ${sent.from} — the reply lands in your inbox.`}
            </p>
            <button className="btn btn--primary btn--sm" style={{ marginTop: 12 }} onClick={onClose}>Done</button>
          </div>
        ) : loaded && !mailbox ? (
          <div className="compose-modal__bd" style={{ textAlign: "center", padding: "30px 20px" }}>
            <b style={{ fontSize: 14.5 }}>Connect a mailbox first</b>
            <p className="faint" style={{ fontSize: 13, marginTop: 6, maxWidth: 320, marginInline: "auto" }}>
              Email sends from your own Gmail or Microsoft 365 mailbox, so replies come back to you.
            </p>
            <Link to="/settings" className="btn btn--primary btn--sm" style={{ marginTop: 14, display: "inline-flex" }} onClick={onClose}><Icon name="plug" size={14} /> Connect in Settings</Link>
          </div>
        ) : (
          <>
            <div className="compose-modal__bd">
              {mailbox && <div className="compose-from"><span className="faint">From</span> <b>{mailbox.email}</b> <span className="tag">{mailbox.provider === "gmail" ? "Gmail" : "Microsoft 365"}{mailbox.simulated ? " · demo" : ""}</span></div>}
              <input className="inp" placeholder="To — email address" value={to} onChange={(e) => setTo(e.target.value)} />
              <input className="inp" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} style={{ marginTop: 8 }} />
              <textarea className="inp" rows={8} placeholder="Write your message…" value={body} onChange={(e) => setBody(e.target.value)} style={{ marginTop: 8 }} />
            </div>
            <div className="compose-modal__ft">
              <span className="faint" style={{ fontSize: 12 }}>{mailbox?.simulated ? "Demo mailbox — recorded, not delivered" : "Sends as you"}</span>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn--sm" onClick={onClose}>Cancel</button>
                <button className="btn btn--primary btn--sm" disabled={!valid || sending} onClick={send}>{sending ? "Sending…" : "Send"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
